import { UserSeatLevel } from '@api/generated/graphql';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { AuthUser } from '@modules/AuthModule/types';
import { MicrophoneAccessType } from '@modules/BrowserModule/types';
import useBrowserState from '@modules/BrowserModule/useBrowserState';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';

/**
 * Custom hook to determine if calling is disabled for the current user.
 *
 * @returns An object containing:
 * - `callingDisabled`: A boolean indicating if calling is disabled.
 * - `reason`: A string providing the reason why calling is disabled, or `undefined` if calling is enabled.
 *
 * The hook checks the following conditions to determine if calling is disabled:
 * 1. If there is no authenticated user.
 * 1. If the user is not configured for outbound calls.
 * 1. If the microphone access is not granted.
 */
const useCallingDisabled = () => {
  const { user } = useAuth();
  const { microphoneAccess } = useBrowserState();
  const { isUnactivatedImpersonator } = usePhoneState();

  const isUserConfiguredForCalling = user?.outboundCallsAllowed ?? false;

  if (!user) {
    return {
      callingDisabled: true,
      reason: 'No user',
    };
  }

  if (user.isImpersonated && isUnactivatedImpersonator) {
    return {
      callingDisabled: true,
      reason: 'Calling is disabled by default when impersonating a user',
    };
  }

  if (!isUserConfiguredForCalling) {
    return {
      callingDisabled: true,
      reason: getReasonForNotConfiguredUser(user as AuthUser),
    };
  }

  if (microphoneAccess !== MicrophoneAccessType.ACCESS_GRANTED) {
    return {
      callingDisabled: true,
      reason: 'Unable to access microphone.',
    };
  }

  return {
    callingDisabled: false,
    reason: undefined,
  };
};

const getReasonForNotConfiguredUser = (user: AuthUser) => {
  let reason = 'Your user account is missing the following configurations:';
  if (
    user.seatLevel != UserSeatLevel.Advanced &&
    user.seatLevel != UserSeatLevel.Basic
  ) {
    reason += ' seat assignment,';
  }
  if (!user.e911Address) {
    reason += ' E911 address,';
  }
  if (!user.personalLine?.phoneNumber) {
    reason += ' phone number,';
  }
  reason = reason.slice(0, -1) + '.';
  return reason;
};

export default useCallingDisabled;
